import React, { useEffect, useState } from 'react';
import { Builder } from '@builder.io/react';
import { useSelector } from 'react-redux';
import { selectSelectedCurrency } from '../../../../../store/user';
import { useKeenSlider } from 'keen-slider/react';
import services from '../../../../../services';
import moment from 'moment';
import AutoplayPlugin from '../../../../../pages/MATWAppeal/components/autoplayPlugin';
import { getCampaign } from '../../../../../pages/MATWAppeal/dates';

const HeartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline', verticalAlign: 'middle' }}>
    <path d="M8 14s6-4.35 6-8.25A3.25 3.25 0 0 0 8 3.25 3.25 3.25 0 0 0 2 5.75C2 9.65 8 14 8 14z" fill="#F60362" stroke="#F60362" strokeWidth="1.2"/>
  </svg>
);

const GetDonorsSlider = ({ campaign = 'palestine', styleOverrides = {}, containerClass = '', donorCardClasses = '' }) => {
  const selectedCurrencyStore = useSelector(selectSelectedCurrency);
  const [donors, setDonors] = useState([]);
  const [sliderRef, instanceRef] = useKeenSlider(
    {
      mode: 'snap',
      loop: true,
      breakpoints: {
        '(min-width: 0px)': {
          slides: { perView: 2, spacing: 4 },
        },
        '(min-width: 600px)': {
          slides: { perView: 3, spacing: 16 },
        },
        '(min-width: 900px)': {
          slides: { perView: 5, spacing: 20 },
        },
        '(min-width: 1200px)': {
          slides: { perView: 6, spacing: 24 },
        },
      },
      slides: { origin: 'center' },
    },
    [AutoplayPlugin(2500)]
  );

  useEffect(() => {
    const fetchDonors = async () => {
      try {
        const req = {
          products: getCampaign(campaign)?.creatorCodes || [],
          limit: 20,
          currency: selectedCurrencyStore || 'AUD',
        };
        const response = await services.getLatestDonors(req);
        setDonors(response.data || []);
      } catch (error) {
        setDonors([]);
      }
    };
    fetchDonors();
  }, [campaign, selectedCurrencyStore]);

  useEffect(() => {
    if (instanceRef.current) {
      instanceRef.current.update();
    }
  }, [donors, instanceRef]);

  const handleDonateTime = (value) => {
    const now = moment();
    const end = moment(value);
    const duration = moment.duration(now.diff(end));
    const mins = Math.abs(duration.asMinutes());
    if (mins < 60) return `${Math.round(mins)} minutes ago`;
    if (mins < 1440) return `${Math.round(mins / 60)} hours ago`;
    if (mins < 10080) return `${Math.round(mins / 1440)} days ago`;
    return `${Math.round(mins / 10080)} weeks ago`;
  };

  return (
    <div
      className={`w-full md:border md:border-[#F60462] bg-[#EAF7FF] px-2 md:px-4 py-3 ${containerClass}`}
      style={styleOverrides}
    >
      <div className="flex flex-col md:flex-row items-center w-full gap-2 min-w-0">
        <span className="text-[18px] md:text-[20px] font-bold text-[#00A3DA] flex-shrink-0" style={{ minWidth: 'fit-content' }}>
          Who just donated?
        </span>
        {donors.length > 0 && (
          <div ref={sliderRef} className="keen-slider flex-1 min-w-0 overflow-hidden">
            {donors.map((item, idx) => (
              <div
                key={idx}
                className="keen-slider__slide flex flex-col items-center justify-center px-2"
                style={{ minWidth: 0 }}
              >
                <div className={`relative border border-[#F60362] bg-white px-4 py-2 flex flex-col items-center w-full max-w-[140px] md:max-w-[300px] mx-auto ${donorCardClasses}`}>
                <span className="absolute top-[2px] right-[6px]"><HeartIcon /></span>
                  <div className="flex items-center justify-center">
                    <span className="text-[15px] text-[#00A3DA] font-bold truncatemax-w-[120px]">
                      {item.donor_name || 'Anonymous'}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#78716C] text-center">
                    {handleDonateTime(item.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

Builder.registerComponent(GetDonorsSlider, {
  name: 'GetDonorsSlider',
  inputs: [
    {
      name: 'campaign',
      type: 'string',
      defaultValue: 'palestine',
      friendlyName: 'Campaign Name',
      helperText: 'Campaign key for fetching donors (e.g., palestine, gaza-bakery)'
    },
    {
      name: 'styleOverrides',
      type: 'object',
      defaultValue: {},
      friendlyName: 'Style Overrides',
      helperText: 'Custom style object for the container'
    },
    {
      name: 'containerClass',
      type: 'string',
      defaultValue: '',
      friendlyName: 'Container Class',
      helperText: 'Extra Tailwind classes for the container'
    },
    {
      name: 'donorCardClasses',
      type: 'string',
      defaultValue: '',
      friendlyName: 'donorCard Styles',
      helperText: 'Extra Tailwind classes for the donor card'
    }
  ]
});

export default GetDonorsSlider;